/* =============================================================
   BOOKINGS — DATA ACCESS
   =============================================================
   Read directly; changed ONLY through the database functions
   respond_to_booking and cancel_booking. The table has no insert
   or update policy at all -- see
   supabase/migrations/20261004113020_create_bookings.sql.

   Staff cannot place a booking from here. request_booking acts
   for the caller's own company, and a platform admin has none.
   Bookings arrive from the app, or from the demo seed.
   ============================================================= */

import { supabase } from './supabase'
import { toDataError } from './errors'
import type { Booking } from './types'

/* Three many-to-one embeds, aliased singular -- the same pattern and the same
 * `overrideTypes` reason as berths.ts. */
const COLUMNS =
  'id, reference, berth_id, vessel_id, company_id, arrival, departure, status, price_eur, rate_id, ' +
  'requested_amenities, decided_at, cancelled_at, created_at, ' +
  'berth:berths(id, name, city, company_id, booking_mode), ' +
  'vessel:vessels(id, name, length_m), ' +
  'company:companies(id, name)'

export interface BookingFilter {
  berthId?: number
  vesselId?: number
  companyId?: number
}

/** Newest arrival first: what is coming up, then what has been. */
export async function listBookings(filter: BookingFilter = {}): Promise<Booking[]> {
  let query = supabase.from('bookings').select(COLUMNS)
  if (filter.berthId !== undefined) query = query.eq('berth_id', filter.berthId)
  if (filter.vesselId !== undefined) query = query.eq('vessel_id', filter.vesselId)
  if (filter.companyId !== undefined) query = query.eq('company_id', filter.companyId)

  const { data, error } = await query
    .order('arrival', { ascending: false })
    .overrideTypes<Booking[], { merge: false }>()

  if (error) throw toDataError(error, 'Could not load bookings.')
  return data ?? []
}

export async function getBooking(id: number): Promise<Booking | null> {
  const { data, error } = await supabase
    .from('bookings')
    .select(COLUMNS)
    .eq('id', id)
    .maybeSingle()
    .overrideTypes<Booking, { merge: false }>()

  if (error) throw toDataError(error, 'Could not load the booking.')
  return data
}

/** Requests waiting for an owner -- the number worth seeing on the dashboard. */
export async function countPendingBookings(): Promise<number> {
  const { count, error } = await supabase
    .from('bookings')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'requested')

  if (error) throw toDataError(error, 'Could not count bookings.')
  return count ?? 0
}

export async function countBookings(): Promise<number> {
  const { count, error } = await supabase
    .from('bookings')
    .select('id', { count: 'exact', head: true })

  if (error) throw toDataError(error, 'Could not count bookings.')
  return count ?? 0
}

/** Accept or reject a request, as the berth owner would. Staff may. */
export async function respondToBooking(id: number, accept: boolean): Promise<void> {
  const { error } = await supabase.rpc('respond_to_booking', {
    p_booking_id: id,
    p_accept: accept,
  })
  if (error) throw toDataError(error, accept ? 'Could not confirm.' : 'Could not reject.')
}

/** Cancel before arrival, as the booker would. Staff may. No fee in the demo. */
export async function cancelBooking(id: number): Promise<void> {
  const { error } = await supabase.rpc('cancel_booking', { p_booking_id: id })
  if (error) throw toDataError(error, 'Could not cancel the booking.')
}
